import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../utils/songUtils';
import SongListItem from './SongListItem';

const SectionContainer = styled.div`
  margin: 50px 0;
  width: 100%;
  box-sizing: border-box;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
  position: relative;

  @media (min-width: 769px) and (max-width: 1280px) {
    padding: 0 15px;
  }

  @media (max-width: 768px) {
    padding: 0;
    margin: 30px 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin-bottom: 30px;
  text-align: left;
  font-size: 1.8em;

  @media (max-width: 768px) {
    text-align: center;
    font-size: 1.4em;
    margin-bottom: 20px;
  }
`;

const SongsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const LoadingContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #2E2E2E;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #ff4444;
  font-size: 16px;
`;

function SimilarStockSection({ currentSong }) {
  const [similarSongs, setSimilarSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSimilarSongs = async () => {
      if (!currentSong) return;

      try {
        setLoading(true);
        setError(null);
        
        // Fetch all songs
        const response = await fetch(`${API_BASE_URL}/songs`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const allSongs = await response.json();
        
        // Filter out the current song
        const otherSongs = allSongs.filter(song => song.song_id !== currentSong.song_id);
        
        // Calculate similarity scores for each song
        const songsWithScores = otherSongs.map(song => {
          let score = 0;
          
          // Count matching tags
          const matchingTags = song.song_tags.filter(tag => 
            currentSong.song_tags.includes(tag)
          );
          score += matchingTags.length * 2; // Give more weight to matching tags
          
          // Compare song titles for common words (excluding common words)
          const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for'];
          const currentWords = currentSong.song_title.toLowerCase()
            .split(' ')
            .filter(word => !commonWords.includes(word));
          const songWords = song.song_title.toLowerCase()
            .split(' ')
            .filter(word => !commonWords.includes(word));
          
          const matchingWords = currentWords.filter(word => 
            songWords.includes(word)
          );
          score += matchingWords.length;
          
          // Add BPM similarity (if within 10 BPM)
          if (Math.abs(song.song_bpm - currentSong.song_bpm) <= 10) {
            score += 1;
          }
          
          // Add key similarity (if same key)
          if (song.song_key === currentSong.song_key) {
            score += 1;
          }
          
          return { ...song, similarityScore: score };
        });
        
        // Sort by similarity score and get top 5 songs with at least 2 points of similarity
        const topSimilarSongs = songsWithScores
          .filter(song => song.similarityScore >= 2)
          .sort((a, b) => b.similarityScore - a.similarityScore)
          .slice(0, 5);
        
        setSimilarSongs(topSimilarSongs);
      } catch (error) {
        console.error('Error fetching similar songs:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilarSongs();
  }, [currentSong]);

  if (loading) {
    return (
      <SectionContainer>
        <SectionTitle>Similar stock</SectionTitle>
        <LoadingContainer>
          Finding similar tracks...
        </LoadingContainer>
      </SectionContainer>
    );
  }

  if (error) {
    return (
      <SectionContainer>
        <SectionTitle>Similar stock</SectionTitle>
        <ErrorContainer>
          Error loading similar tracks. Please try again later.
        </ErrorContainer>
      </SectionContainer>
    );
  }

  if (similarSongs.length === 0) {
    return (
      <SectionContainer>
        <SectionTitle>Similar stock</SectionTitle>
        <LoadingContainer>
          No similar tracks found at the moment.
        </LoadingContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionTitle>Similar stock</SectionTitle>
      <SongsList>
        {similarSongs.map((song) => (
          <SongListItem 
            key={song.song_id} 
            song={song}
            shouldTrackPlay={false}
          />
        ))}
      </SongsList>
    </SectionContainer>
  );
}

export default SimilarStockSection; 